import { Box, Typography, useTheme } from '@mui/material';

import { AdFooter, AnalyticsContext, Maybe, NavigationLink } from '@/types/generated';
import TAGLink from '@/components/TAGLink';
import { useAppContext, useFacilityContext, useOfficeLocationContext } from '@/context';
import { resolveLinkTitle, resolveLinkUrl } from '@/components/FooterLite/FooterLite.helpers';
import { ONE_TRUST_TOGGLE } from '@/constants';
import { PrivacyOptions } from '@/assets/SVG';
import { isNavLink } from '@/components/Footer/Footer.helpers';

import {
	getWrapperStyles,
	getNavLinkStyles,
	getNavTitleStyles,
	footerLinkTextBoxDesktopStyles,
} from './FooterNavigationContent.styles';

export interface IFooterNavigationContent {
	navigationGroups: Maybe<AdFooter['navigationGroupsCollection']>;
	isLightFooter?: boolean;
}

declare global {
	interface Window {
		OneTrust?: {
			ToggleInfoDisplay: () => void;
		};
	}
}

const isOneTrustLink = (navigationLink: NavigationLink | null) => {
	return navigationLink && isNavLink(navigationLink) && navigationLink.externalUrl?.includes(ONE_TRUST_TOGGLE);
};

export default function FooterNavigationContent({ navigationGroups, isLightFooter }: IFooterNavigationContent) {
	const theme = useTheme();
	const { config } = useAppContext();
	const { officeInfo } = useFacilityContext();
	const { officeInfo: officeLocationInfo } = useOfficeLocationContext();
	const contextData = {
		config,
		officeInfo,
		officeLocationInfo,
	};

	const groupItems = navigationGroups?.items;

	if (!groupItems) return null;

	return (
		<Box data-test-id="section_footer_navigation_content" sx={{ ...getWrapperStyles() }}>
			{groupItems.map((group, index) =>
				group ? (
					<Box key={index}>
						{group?.navigationGroupTitle ? (
							<Typography
								data-test-id={`link_group_title_${group.sys.id}`}
								variant="bodyLargeBold"
								title={group.navigationGroupTitle}
								color={isLightFooter ? 'text.interactive' : 'text.light'}
							>
								{group.navigationGroupTitle}
							</Typography>
						) : group?.navigationGroupLink ? (
							<TAGLink
								dataTestId={`link_group_title_${group.sys.id}`}
								href={resolveLinkUrl(group.navigationGroupLink, contextData)}
								analyticsContext={
									group.navigationGroupLink.analyticsContext ||
									({ clickPosition: 'footer' } as AnalyticsContext)
								}
							>
								<Typography
									color={isLightFooter ? 'text.interactive' : 'text.light'}
									sx={{ ...getNavTitleStyles() }}
									title={resolveLinkTitle(group.navigationGroupLink)}
								>
									{resolveLinkTitle(group.navigationGroupLink).toUpperCase()}
								</Typography>
							</TAGLink>
						) : null}

						{group.navigationLinksForGroupCollection?.items.map((navigationItem) =>
							navigationItem ? (
								<TAGLink
									key={navigationItem.sys.id}
									href={resolveLinkUrl(navigationItem, contextData)}
									openInNewTab={(navigationItem as NavigationLink)?.openInNewTab || false}
									dataTestId={`link_for_group_${navigationItem.sys.id}`}
									noLinkStyle
									analyticsContext={
										navigationItem.analyticsContext ||
										({ clickPosition: 'footer' } as AnalyticsContext)
									}
									onClick={
										isOneTrustLink(navigationItem as NavigationLink)
											? (e) => {
													e.preventDefault();
													window?.OneTrust?.ToggleInfoDisplay();
											  }
											: undefined
									}
								>
									<Box
										sx={footerLinkTextBoxDesktopStyles(
											isOneTrustLink(navigationItem as NavigationLink) || false
										)}
									>
										{isOneTrustLink(navigationItem as NavigationLink) && (
											<PrivacyOptions
												dataTestId={`privacy_icon_${navigationItem.sys.id}`}
												verticalAlign="bottom"
											/>
										)}
										<Typography
											color={isLightFooter ? 'text.interactive' : 'text.light'}
											variant="bodyMediumBook"
											title={resolveLinkTitle(navigationItem)}
											sx={getNavLinkStyles(theme)}
										>
											{resolveLinkTitle(navigationItem)}
										</Typography>
									</Box>
								</TAGLink>
							) : null
						)}
					</Box>
				) : null
			)}
		</Box>
	);
}
